/**
 * Step-up auth modal (Task #3066).
 *
 * Shown when an API call returns 401 `{ error: "step_up_required" }`. The
 * user re-proves identity (password today; MFA TOTP if a factor is
 * enrolled) and on success we re-run the original request.
 *
 * Wiring
 *   - `withStepUp(fetcher)` (lib/step-up.ts) catches the structured error
 *     and dispatches a window event that this dialog listens for.
 *   - The dialog calls `POST /api/auth/step-up` with the chosen method;
 *     on 200 it resolves the in-flight promise so the original action
 *     can retry. On cancel/failure it rejects so the caller can surface
 *     the error inline.
 *
 * Why this lives at the AuthProvider layer
 *   The same modal is reused by every screen that touches a sensitive
 *   action (password, email, payouts, payment methods, sessions, account
 *   delete). Mounting it once at the root + dispatching via the window
 *   event keeps the per-form code small (one `withStepUp` wrap) and
 *   guarantees we never end up with two dialogs racing.
 *
 * Why shadcn Dialog (not a raw div)
 *   Radix Dialog applies a focus trap and sets aria-hidden on everything
 *   outside its portal. When step-up fires while another Radix Dialog is
 *   still open (e.g. the impersonation reason dialog), a raw-div overlay
 *   is aria-hidden by the outer trap and becomes un-interactive. Using
 *   Radix Dialog here means it stacks correctly above any sibling dialog
 *   regardless of call site.
 */
import { useCallback, useEffect, useState } from "react";
import {
  SENSITIVE_ACTIONS,
  type SensitiveAction,
} from "@workspace/api-zod";
import { useUser } from "@/hooks/useUser";
import { getSupabaseClient } from "@/lib/supabase/client";
import { STEP_UP_EVENT, type StepUpEventDetail } from "@/lib/step-up-event";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@workspace/ui/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@workspace/ui/components/ui/input";

// Canonical definitions live in lib/step-up-event so non-component modules can
// reference them without importing "up" into the components chunk. Re-exported
// here for backward compatibility with any consumer importing from this module.
export { STEP_UP_EVENT };
export type { StepUpEventDetail };

interface ModalState {
  action: SensitiveAction;
  resolve: () => void;
  reject: (err: Error) => void;
}

type Mode = "password" | "otp" | "mfa";

export function StepUpAuthDialog() {
  const { t } = useAppTranslation();
  const { session } = useUser();
  const [state, setState] = useState<ModalState | null>(null);
  const [mode, setMode] = useState<Mode>("password");
  const [password, setPassword] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [otp, setOtp] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  // TOTP authenticator availability. Resolved lazily when the dialog
  // opens by listing the current session's verified factors, so a user
  // who enrolled an authenticator can step up with their strongest
  // factor instead of silently falling back to the email code.
  const [totpFactorId, setTotpFactorId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onRequired(e: Event) {
      const detail = (e as CustomEvent<StepUpEventDetail>).detail;
      // If another step-up is already showing, reject the new one to
      // avoid stacking modals; the caller can surface its own toast.
      if (state) {
        detail.reject(new Error("Another confirmation is already in progress"));
        return;
      }
      setPassword("");
      setOtp("");
      setMfaCode("");
      setOtpRequested(false);
      setTotpFactorId(null);
      setMode("password");
      setError(null);
      setState({ action: detail.action, resolve: detail.resolve, reject: detail.reject });
    }
    window.addEventListener(STEP_UP_EVENT, onRequired as EventListener);
    return () => window.removeEventListener(STEP_UP_EVENT, onRequired as EventListener);
  }, [state]);

  // When the dialog opens, discover whether the user has a verified TOTP
  // factor so we can offer the Authenticator option. `listFactors()`
  // returns only verified factors in its `totp` convenience array, so a
  // half-finished enrolment never shows up here.
  useEffect(() => {
    if (!state) return;
    let cancelled = false;
    const supabase = getSupabaseClient();
    if (!supabase) return;
    supabase.auth.mfa
      .listFactors()
      .then(({ data }) => {
        if (cancelled) return;
        const factor = data?.totp?.[0];
        setTotpFactorId(factor?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setTotpFactorId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  const cancel = useCallback(() => {
    if (!state) return;
    state.reject(new Error("step_up_cancelled"));
    setState(null);
  }, [state]);

  const postStepUp = useCallback(
    async (body: Record<string, unknown>): Promise<Response> => {
      const token = session?.access_token;
      if (!token) throw new Error("not_signed_in");
      return fetch(`${import.meta.env.BASE_URL || "/"}api/auth/step-up`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
    },
    [session?.access_token],
  );

  // Task #6191 — the per-account step-up throttle sits BEFORE method handling,
  // so any tab can receive a rate-limit outcome. Distinguish a fail-CLOSED
  // transient store outage (503 `verification_unavailable` → "temporarily
  // unavailable") from a genuine over-limit lockout (429 → "too many attempts")
  // so users never see a false "Too many attempts". Returns null when the
  // response isn't a rate-limit outcome, letting the caller fall through to its
  // method-specific messaging.
  const rateLimitMessage = useCallback(
    (status: number, code: string): string | null => {
      if (status === 503 || code === "verification_unavailable") return t("auth.stepup_temporarily_unavailable");
      if (status === 429) return t("auth.stepup_too_many_attempts");
      return null;
    },
    [t],
  );

  const submit = useCallback(async () => {
    if (!state) return;
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "password") {
        const res = await postStepUp({ method: "password", password });
        if (res.status === 409) {
          // password_not_set — OAuth-only account. Pivot to OTP.
          setMode("otp");
          setError("This account has no password. We'll email you a code instead.");
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = typeof body?.error === "string" ? body.error : "";
          const rl = rateLimitMessage(res.status, code);
          if (rl) { setError(rl); return; }
          setError(code === "Incorrect password"
            ? "Incorrect password. Try again."
            : code || `Re-auth failed (${res.status})`);
          return;
        }
      } else if (mode === "mfa") {
        if (!totpFactorId) {
          setError("No authenticator is set up on this account.");
          return;
        }
        const supabase = getSupabaseClient();
        if (!supabase) { setError("Authenticator check unavailable. Try another method."); return; }
        // A fresh challenge per submit — GoTrue ties the code to a
        // specific challenge id, and the server verifies the same
        // factor/challenge/code triple against the bearer token.
        const { data: challenge, error: challengeError } =
          await supabase.auth.mfa.challenge({ factorId: totpFactorId });
        if (challengeError || !challenge) {
          setError("Couldn't start the authenticator check. Try again.");
          return;
        }
        const res = await postStepUp({
          method: "mfa",
          factorId: totpFactorId,
          challengeId: challenge.id,
          code: mfaCode,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = typeof body?.error === "string" ? body.error : "";
          const rl = rateLimitMessage(res.status, code);
          if (rl) { setError(rl); return; }
          setError(code || "Invalid authenticator code.");
          return;
        }
      } else {
        if (!otpRequested) {
          const res = await postStepUp({ method: "otp_request" });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            const code = typeof body?.error === "string" ? body.error : "";
            const rl = rateLimitMessage(res.status, code);
            if (rl) { setError(rl); return; }
            // Surface the actual reason rather than a blanket "couldn't send".
            if (code === "no_email_on_file") setError(t("auth.stepup_no_email"));
            else setError(t("auth.stepup_code_send_failed"));
            return;
          }
          setOtpRequested(true);
          setError("Code sent. Check your email.");
          return;
        }
        const res = await postStepUp({ method: "otp", code: otp });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = typeof body?.error === "string" ? body.error : "";
          const rl = rateLimitMessage(res.status, code);
          if (rl) { setError(rl); return; }
          setError(code || "Invalid or expired code.");
          return;
        }
      }
      state.resolve();
      setState(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Re-auth failed");
    } finally {
      setSubmitting(false);
    }
  }, [state, mode, password, otp, otpRequested, mfaCode, totpFactorId, postStepUp]);

  const cfg = state ? SENSITIVE_ACTIONS[state.action] : null;

  return (
    <Dialog open={state !== null} onOpenChange={(open) => { if (!open) cancel(); }}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle>{t("auth.stepup_title")}</DialogTitle>
          <DialogDescription>
            {t("auth.stepup_reauth_prompt", { action: cfg?.label || t("auth.stepup_this_action") })}
          </DialogDescription>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="flex gap-1.5">
          <Button
            type="button"
            size="sm"
            variant={mode === "password" ? "default" : "outline"}
            onClick={() => { setMode("password"); setError(null); }}
            disabled={submitting}
            aria-pressed={mode === "password"}
            className="flex-1"
          >
            Password
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "otp" ? "default" : "outline"}
            onClick={() => { setMode("otp"); setError(null); }}
            disabled={submitting}
            aria-pressed={mode === "otp"}
            className="flex-1"
          >
            {t("auth.stepup_method_email")}
          </Button>
          {totpFactorId && (
            <Button
              type="button"
              size="sm"
              variant={mode === "mfa" ? "default" : "outline"}
              onClick={() => { setMode("mfa"); setError(null); }}
              disabled={submitting}
              aria-pressed={mode === "mfa"}
              className="flex-1"
            >
              {t("auth.stepup_method_authenticator")}
            </Button>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-3">
          {mode === "password" ? (
            <div className="space-y-1.5">
              <label htmlFor="step-up-password" className="text-sm font-medium">
                Password
              </label>
              <Input
                id="step-up-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>
          ) : mode === "mfa" ? (
            <div className="space-y-1.5">
              <label htmlFor="step-up-mfa" className="text-sm font-medium">
                {t("auth.stepup_mfa_label")}
              </label>
              <Input
                id="step-up-mfa"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                disabled={submitting}
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <label htmlFor="step-up-otp" className="text-sm font-medium">
                {otpRequested ? t("auth.stepup_otp_enter") : t("auth.stepup_otp_offer")}
              </label>
              {otpRequested && (
                <Input
                  id="step-up-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={submitting}
                />
              )}
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={cancel}
              disabled={submitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                submitting
                || (mode === "password" && !password)
                || (mode === "otp" && otpRequested && !otp)
                || (mode === "mfa" && !mfaCode)
              }
            >
              {submitting
                ? t("auth.stepup_confirming")
                : mode === "otp" && !otpRequested
                  ? t("auth.stepup_send_code")
                  : t("auth.stepup_confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
